/**
 * Client-side router on the History API.
 *
 * <p>
 * Routes are plain objects: a path pattern with {@code :name} segments, a title and a
 * {@code render(params)} function that returns the page template. The router is a Lit reactive
 * controller: the host re-renders on every navigation and asks {@link Router#outlet} for the page.
 * </p>
 *
 * <p>
 * Links are ordinary {@code <a href>}: a click on a link to a client route is intercepted, also
 * inside shadow roots. Links to what the backend serves itself (Dash under {@code /-/}, the API,
 * assets) are left to the browser.
 * </p>
 */

/** Path prefixes the backend serves: never handled on the client. */
const SERVER_PREFIXES = ['/-/', '/api/', '/assets/', '/clawlet/'];

/**
 * Compiles a path pattern such as {@code /servers/:serverId} into a matcher.
 *
 * @returns {(path: string) => Record<string, string> | null} the decoded parameters, or null
 */
export function compile(pattern) {
  const names = [];
  const source = pattern
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        names.push(segment.slice(1));
        return '([^/]+)';
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  const regex = new RegExp(`^${source}/?$`);
  return (path) => {
    const found = regex.exec(path);
    if (!found) {
      return null;
    }
    const params = {};
    try {
      names.forEach((name, i) => {
        params[name] = decodeURIComponent(found[i + 1]);
      });
    } catch {
      return null;
    }
    return params;
  };
}

/**
 * Finds the first route matching a path. A route without {@code path} matches anything: put it
 * last, as the "not found" page.
 *
 * @returns {{ route: object, params: Record<string, string> } | null}
 */
export function match(routes, path) {
  for (const route of routes) {
    if (route.path === undefined) {
      return { route, params: {} };
    }
    route.matcher ??= compile(route.path);
    const params = route.matcher(path);
    if (params) {
      return { route, params };
    }
  }
  return null;
}

/** Whether a path belongs to the client, as opposed to the backend. */
export function isClientPath(path) {
  return !SERVER_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export class Router {
  /**
   * @param host the Lit element that renders {@link Router#outlet}
   * @param routes the route table, most specific first, "not found" last
   * @param appTitle the suffix of the document title
   */
  constructor(host, routes, appTitle) {
    this.host = host;
    this.routes = routes;
    this.appTitle = appTitle;
    this.current = null;
    this.onPopState = () => this.resolve();
    this.onClick = (event) => this.intercept(event);
    host.addController(this);
  }

  hostConnected() {
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('click', this.onClick);
    this.resolve();
  }

  hostDisconnected() {
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('click', this.onClick);
  }

  /** The path of the current route, e.g. to mark the active link. */
  get path() {
    return window.location.pathname;
  }

  /** Navigates to a client path, adding a history entry. */
  navigate(path) {
    if (path !== this.path + window.location.search + window.location.hash) {
      window.history.pushState({}, '', path);
    }
    this.resolve();
    window.scrollTo(0, 0);
  }

  /** The template of the current page. */
  outlet() {
    return this.current ? this.current.route.render(this.current.params) : null;
  }

  resolve() {
    this.current = match(this.routes, this.path);
    const title = this.current?.route.title;
    const text = typeof title === 'function' ? title(this.current.params) : title;
    document.title = text ? `${text} · ${this.appTitle}` : this.appTitle;
    this.host.requestUpdate();
  }

  intercept(event) {
    if (event.defaultPrevented || event.button !== 0) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const anchor = event.composedPath().find((node) => node instanceof HTMLAnchorElement);
    if (!anchor || !anchor.href || anchor.hasAttribute('download')) {
      return;
    }
    if (anchor.target && anchor.target !== '_self') {
      return;
    }
    const url = new URL(anchor.href);
    if (url.origin !== window.location.origin || !isClientPath(url.pathname)) {
      return;
    }
    event.preventDefault();
    this.navigate(url.pathname + url.search + url.hash);
  }
}

/**
 * Navigates to a client path from anywhere, e.g. a page after a save: adds a history entry and
 * lets the router resolve it as it does for the back button.
 */
export function navigate(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}
